import RecurringTaskManagementModal from '@/components/modals/recurring-task-management-modal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar as CalendarIcon,
  ChevronDown,
  FileText,
  LayoutGrid,
  Lock,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  User,
  Users,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';

type SidebarProps = {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

export default function Sidebar({
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const { user } = useAuth();
  const [location] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // State for modals
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [recurringTaskManagementOpen, setRecurringTaskManagementOpen] =
    useState(false);

  // Mobile state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Collapsed state (controlled or uncontrolled with persistence)
  const isControlled = typeof onToggleCollapse === 'function';
  const [internalCollapsed, setInternalCollapsed] = useState(collapsed);

  useEffect(() => {
    if (isControlled) {
      setInternalCollapsed(collapsed);
    }
  }, [collapsed, isControlled]);

  useEffect(() => {
    if (isControlled || !user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setInternalCollapsed(stored === 'true');
    }
  }, [user?.id, isControlled]);

  useEffect(() => {
    if (isControlled || !user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    window.localStorage.setItem(key, String(internalCollapsed));
  }, [internalCollapsed, user?.id, isControlled]);

  const isCollapsed = isControlled ? collapsed : internalCollapsed;
  const handleToggleCollapse = () => {
    if (isControlled && onToggleCollapse) {
      onToggleCollapse();
    } else {
      setInternalCollapsed(prev => !prev);
    }
  };

  // State for forms
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // State for profile form (admin only)
  const [profileForm, setProfileForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    newPassword: '',
    confirmNewPassword: '',
    currentPassword: '',
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: {
      currentPassword: string;
      newPassword: string;
    }) => {
      await apiRequest('PATCH', '/api/user/change-password', data);
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Password changed successfully',
      });
      setPasswordOpen(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to change password',
        variant: 'destructive',
      });
    },
  });

  // Update profile mutation (admin only)
  const updateProfileMutation = useMutation({
    mutationFn: async (data: {
      username?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      newPassword?: string;
      currentPassword?: string;
    }) => {
      const payload: any = {};
      if (data.username) payload.username = data.username;
      if (data.firstName !== undefined) payload.firstName = data.firstName;
      if (data.lastName !== undefined) payload.lastName = data.lastName;
      if (data.email !== undefined) payload.email = data.email;

      // If password change is requested
      if (data.newPassword && data.currentPassword) {
        // First change password - apiRequest will throw on error
        await apiRequest('PATCH', '/api/user/change-password', {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        });
      }

      // Then update profile if there are other changes
      if (Object.keys(payload).length > 0) {
        await apiRequest('PATCH', `/api/users/${user?.id}`, payload);
      }
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
      setProfileOpen(false);
      queryClient.invalidateQueries({ queryKey: ['/api/me'] });
      // Reset form to current user data
      if (user) {
        setProfileForm({
          username: user.username || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          newPassword: '',
          confirmNewPassword: '',
          currentPassword: '',
        });
      }
    },
    onError: (error: any) => {
      // Parse error message from apiRequest format "500: {...}"
      let errorMessage = 'Failed to update profile';
      if (error.message) {
        const match = error.message.match(/^\d+:\s*(.+)$/);
        if (match) {
          try {
            const errorJson = JSON.parse(match[1]);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            errorMessage = match[1] || errorMessage;
          }
        } else {
          errorMessage = error.message;
        }
      }
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // Initialize profile form when user data changes or modal opens
  useEffect(() => {
    if (user && profileOpen) {
      setProfileForm({
        username: user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        newPassword: '',
        confirmNewPassword: '',
        currentPassword: '',
      });
    }
  }, [user, profileOpen]);

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || user.role !== 'admin') {
      return;
    }

    // Validate password if changing
    if (profileForm.newPassword) {
      if (!profileForm.currentPassword) {
        toast({
          title: 'Error',
          description: 'Current password is required to change password',
          variant: 'destructive',
        });
        return;
      }

      if (profileForm.newPassword !== profileForm.confirmNewPassword) {
        toast({
          title: 'Error',
          description: 'New passwords do not match',
          variant: 'destructive',
        });
        return;
      }

      if (profileForm.newPassword.length < 6) {
        toast({
          title: 'Error',
          description: 'Password must be at least 6 characters long',
          variant: 'destructive',
        });
        return;
      }
    }

    const mutationData: {
      username?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      newPassword?: string;
      currentPassword?: string;
    } = {
      username: profileForm.username,
      firstName: profileForm.firstName,
      lastName: profileForm.lastName,
      email: profileForm.email,
    };

    // Only include password fields if they are provided
    if (profileForm.newPassword && profileForm.currentPassword) {
      mutationData.newPassword = profileForm.newPassword;
      mutationData.currentPassword = profileForm.currentPassword;
    }

    updateProfileMutation.mutate(mutationData);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive',
      });
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return 'U';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const isActive = (path: string) => {
    if (path === '/' && location === '/') return true;
    if (path !== '/' && location === path) return true;
    return false;
  };

  const canManage = user?.role === 'pm' || user?.role === 'gm' || user?.role === 'admin';
  const canRequestHelp =
    user?.role === 'team_member' ||
    user?.role === 'pm' ||
    user?.role === 'gm' ||
    user?.role === 'admin';

  const [openSection, setOpenSection] = useState<string>('');

  const toggleSection = (title: string) => {
    setOpenSection(prev => (prev === title ? '' : title));
  };

  // Auto-open the section that contains the active route
  useEffect(() => {
    const activeSection = navSections.find(section =>
      section.items.some(item => isActive(item.path))
    );
    if (activeSection && activeSection.title !== openSection) {
      setOpenSection(activeSection.title);
    }
  }, [location]);

  // Board View as a standalone top-level item
  const boardViewItem = {
    icon: LayoutGrid,
    label: 'Board View',
    path: '/',
    testId: 'nav-board',
  };

  // Settings as a standalone top-level item
  const settingsItem = {
    icon: Settings,
    label: 'Settings',
    path: '/settings',
    testId: 'nav-settings',
  };

  const navSections = [
    {
      title: 'Reports',
      items: [
        {
          icon: FileText,
          label: 'Task Analysis',
          path: '/task-analysis',
          testId: 'nav-task-analysis',
          requiresManagement: true,
        },
        // {
        //   icon: CheckCircle,
        //   label: 'Completed Tasks',
        //   path: '/completed-tasks',
        //   testId: 'nav-completed',
        // },
        {
          icon: CalendarIcon,
          label: 'Calendar',
          path: '/calendar',
          testId: 'nav-calendar',
        },
      ],
    },
    {
      title: 'Masters',
      items: [
        {
          icon: Users,
          label: 'Team Management',
          path: '/team-management',
          testId: 'nav-teams',
          requiresManagement: true,
        },
        // {
        //   icon: FolderOpen,
        //   label: 'Projects',
        //   path: '/projects',
        //   testId: 'nav-projects',
        // },
      ],
    },
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-white shadow-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-4 w-4" />
          ) : (
            <Menu className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
        w-64 ${isCollapsed ? 'lg:w-20' : 'lg:w-64'} bg-surface dark:bg-surface shadow-lg border-r border-gray-200 dark:border-gray-700 flex flex-col
        fixed lg:relative h-full z-50
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
      >
        {/* Header */}
        <div
          className={`relative p-6 border-b border-gray-200 dark:border-gray-700 ${isCollapsed ? 'lg:p-4' : ''}`}
        >
          <div
            className={`flex items-center space-x-3 justify-center`}
          >
            <img
              src="/suryacon-logo.png"
              alt="Suryacon"
              className={`w-auto ${isCollapsed ? 'h-9 lg:h-9' : 'h-14 lg:h-14'}`}
              loading="lazy"
            />
            <span
              className={`text-xl font-bold text-gray-900 dark:text-gray-100 ${isCollapsed ? 'lg:hidden' : ''}`}
            >
              &nbsp;
            </span>
          </div>
          {/* Desktop collapse toggle inside sidebar */}
          {(onToggleCollapse || !isControlled) && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex absolute top-2 lg:-right-4 h-8 w-8 border border-gray-200 bg-white/90 dark:bg-gray-800/90 shadow-sm"
              onClick={handleToggleCollapse}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <PanelLeftOpen className="h-4 w-4" />
              ) : (
                <PanelLeftClose className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* User Profile */}
          {user && (
            <div
              className={`mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg ${isCollapsed ? 'lg:flex lg:justify-center lg:p-2' : ''}`}
            >
              <div
                className={`flex items-center space-x-3 ${isCollapsed ? 'lg:justify-center' : ''}`}
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user.profileImageUrl || ''} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                    {getInitials(
                      user.firstName || undefined,
                      user.lastName || undefined
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className={`flex-1 min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {user.firstName} {user.lastName}
                  </p>
                  <Badge
                    variant={
                      user.role === 'admin'
                        ? 'destructive'
                        : user.role === 'pm' || user.role === 'gm'
                          ? 'default'
                          : 'secondary'
                    }
                    className="text-xs"
                  >
                    {user.role === 'pm' ? 'PM' : user.role === 'gm' ? 'GM' : user.role.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 p-4 space-y-4 ${isCollapsed ? 'lg:p-3' : ''}`}>
          <ul className="space-y-4">
            {/* Board View as top-level item */}
            <li>
              {(() => {
                const Icon = boardViewItem.icon;
                const active = isActive(boardViewItem.path);
                return (
                  <Link href={boardViewItem.path}>
                    <Button
                      variant="ghost"
                      className={`w-full justify-start rounded-md transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        isCollapsed ? 'lg:justify-center lg:px-2' : ''
                      } ${
                        active
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary dark:hover:bg-primary/20 dark:hover:text-primary-foreground'
                      }`}
                      data-testid={boardViewItem.testId}
                      title={boardViewItem.label}
                      aria-label={boardViewItem.label}
                    >
                      <Icon
                        className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`}
                      />
                      <span className={isCollapsed ? 'lg:hidden' : ''}>
                        {boardViewItem.label}
                      </span>
                    </Button>
                  </Link>
                );
              })()}
            </li>

            {navSections
              .filter(section => {
                // Show Reports and Masters sections only to admins
                if (section.title === 'Reports' || section.title === 'Masters') {
                  return user?.role === 'admin';
                }
                return true;
              })
              .map(section => {
              const visibleItems = section.items.filter(item => {
                if (item.requiresManagement && !canManage) return false;
                if ((item as any).requiresStaffOrManager && !canRequestHelp) return false;
                return true;
              });

              if (visibleItems.length === 0) return null;

              const isOpen = isCollapsed ? true : openSection === section.title;

              return (
                <li key={section.title} className="space-y-2">
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${
                      isCollapsed ? 'lg:hidden' : ''
                    }`}
                    onClick={() => toggleSection(section.title)}
                  >
                    <span>{section.title}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        isOpen ? 'rotate-0' : '-rotate-90'
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <ul className="space-y-2">
                      {visibleItems.map(item => {
                        const Icon = item.icon;
                        const active = isActive(item.path);

                        return (
                          <li key={item.path}>
                            <Link href={item.path}>
                              <Button
                                variant="ghost"
                        className={`w-full justify-start rounded-md transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                          isCollapsed ? 'lg:justify-center lg:px-2' : ''
                        } ${
                          active
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary dark:hover:bg-primary/20 dark:hover:text-primary-foreground'
                        }`}
                                data-testid={item.testId}
                                title={item.label}
                                aria-label={item.label}
                              >
                                <Icon
                                  className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`}
                                />
                                <span className={isCollapsed ? 'lg:hidden' : ''}>
                                  {item.label}
                                </span>
                              </Button>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}

          </ul>
        </nav>

        {/* Footer */}
        <div
          className={`p-4 border-t border-gray-200 dark:border-gray-700 ${isCollapsed ? 'lg:p-3' : ''}`}
        >
          <div className="space-y-2">
            {/* View Profile Modal */}
            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className={`w-full justify-start text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    isCollapsed ? 'lg:justify-center lg:px-2' : ''
                  }`}
                  data-testid="button-view-profile"
                  title="View Profile"
                  aria-label="View Profile"
                >
                  <User
                    className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`}
                  />
                  <span className={isCollapsed ? 'lg:hidden' : ''}>
                    View Profile
                  </span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>User Profile</DialogTitle>
                  <DialogDescription>
                    {user?.role === 'admin'
                      ? 'Edit your account information (Admin only)'
                      : 'Your account information'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleProfileSubmit}>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="username" className="text-right">
                        Username
                      </Label>
                      <Input
                        id="username"
                        value={profileForm.username}
                        onChange={e => setProfileForm({ ...profileForm, username: e.target.value })}
                        className="col-span-3"
                        disabled={user?.role !== 'admin'}
                      />
                    </div>
                    {/* <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="email" className="text-right">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                        className="col-span-3"
                        disabled={user?.role !== 'admin'}
                      />
                    </div> */}
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="firstName" className="text-right">
                        First Name
                      </Label>
                      <Input
                        id="firstName"
                        value={profileForm.firstName}
                        onChange={e => setProfileForm({ ...profileForm, firstName: e.target.value })}
                        className="col-span-3"
                        disabled={user?.role !== 'admin'}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="lastName" className="text-right">
                        Last Name
                      </Label>
                      <Input
                        id="lastName"
                        value={profileForm.lastName}
                        onChange={e => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        className="col-span-3"
                        disabled={user?.role !== 'admin'}
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="role" className="text-right">
                        Role
                      </Label>
                      <Input
                        id="role"
                        value={user?.role?.replace('_', ' ') || ''}
                        className="col-span-3"
                        disabled
                      />
                    </div>
                    {/* <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="team" className="text-right">
                        Team
                      </Label>
                      <Input
                        id="team"
                        value={user?.teamId || 'No team assigned'}
                        className="col-span-3"
                        disabled
                      />
                    </div> */}

                    {/* Password Change Section - Admin Only */}
                    {user?.role === 'admin' && (
                      <>
                        <div className="border-t pt-4 mt-2">
                          <Label className="text-sm font-semibold mb-4 block">Change Password (Optional)</Label>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="currentPassword" className="text-right text-sm">
                              Current
                            </Label>
                            <Input
                              id="currentPassword"
                              type="password"
                              value={profileForm.currentPassword}
                              onChange={e => setProfileForm({ ...profileForm, currentPassword: e.target.value })}
                              className="col-span-3"
                              placeholder="Enter current password"
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4 mt-2">
                            <Label htmlFor="newPassword" className="text-right text-sm">
                              New
                            </Label>
                            <Input
                              id="newPassword"
                              type="password"
                              value={profileForm.newPassword}
                              onChange={e => setProfileForm({ ...profileForm, newPassword: e.target.value })}
                              className="col-span-3"
                              placeholder="Enter new password"
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4 mt-2">
                            <Label htmlFor="confirmNewPassword" className="text-right text-sm">
                              Confirm
                            </Label>
                            <Input
                              id="confirmNewPassword"
                              type="password"
                              value={profileForm.confirmNewPassword}
                              onChange={e => setProfileForm({ ...profileForm, confirmNewPassword: e.target.value })}
                              className="col-span-3"
                              placeholder="Confirm new password"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setProfileOpen(false)}
                    >
                      Close
                    </Button>
                    {user?.role === 'admin' && (
                      <Button
                        type="submit"
                        disabled={updateProfileMutation.isPending}
                      >
                        {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Recurring Task Management */}
            {/* <Button
              variant="ghost"
              className={`w-full justify-start text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                isCollapsed ? 'lg:justify-center lg:px-2' : ''
              }`}
              onClick={() => setRecurringTaskManagementOpen(true)}
              data-testid="button-recurring-tasks"
              title="Manage Recurring Tasks"
              aria-label="Manage Recurring Tasks"
            >
              <Repeat className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`} />
              <span className={isCollapsed ? 'lg:hidden' : ''}>
                Manage Recurring Tasks
              </span>
            </Button> */}

            {/* Change Password Modal */}
            <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  className={`w-full justify-start text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    isCollapsed ? 'lg:justify-center lg:px-2' : ''
                  }`}
                  data-testid="button-change-password"
                  title="Change Password"
                  aria-label="Change Password"
                >
                  <Lock
                    className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`}
                  />
                  <span className={isCollapsed ? 'lg:hidden' : ''}>
                    Change Password
                  </span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                  <DialogDescription>
                    Enter your current password and a new password
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePasswordSubmit}>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="currentPassword" className="text-right">
                        Current
                      </Label>
                      <Input
                        id="currentPassword"
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={e =>
                          setPasswordForm(prev => ({
                            ...prev,
                            currentPassword: e.target.value,
                          }))
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="newPassword" className="text-right">
                        New
                      </Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={e =>
                          setPasswordForm(prev => ({
                            ...prev,
                            newPassword: e.target.value,
                          }))
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="confirmPassword" className="text-right">
                        Confirm
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={e =>
                          setPasswordForm(prev => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        className="col-span-3"
                        required
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPasswordOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={changePasswordMutation.isPending}
                    >
                      {changePasswordMutation.isPending
                        ? 'Changing...'
                        : 'Change Password'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Settings */}
            <Link href={settingsItem.path}>
              <Button
                variant="ghost"
                className={`w-full justify-start text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  isCollapsed ? 'lg:justify-center lg:px-2' : ''
                } ${
                  isActive(settingsItem.path)
                    ? 'bg-gray-100 dark:bg-gray-800'
                    : ''
                }`}
                data-testid={settingsItem.testId}
                title={settingsItem.label}
                aria-label={settingsItem.label}
              >
                <Settings
                  className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`}
                />
                <span className={isCollapsed ? 'lg:hidden' : ''}>
                  {settingsItem.label}
                </span>
              </Button>
            </Link>

            <Button
              variant="ghost"
              className={`w-full justify-start text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ${
                isCollapsed ? 'lg:justify-center lg:px-2' : ''
              }`}
              onClick={async () => {
                try {
                  await apiRequest('POST', '/api/logout');
                  queryClient.clear();
                  window.location.reload();
                } catch (error) {
                  console.error('Logout failed:', error);
                }
              }}
              data-testid="button-logout"
              title="Sign Out"
              aria-label="Sign Out"
            >
              <LogOut
                className={`w-5 h-5 ${isCollapsed ? 'lg:mr-0' : 'mr-3'}`}
              />
              <span className={isCollapsed ? 'lg:hidden' : ''}>Sign Out</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Recurring Task Management Modal */}
      <RecurringTaskManagementModal
        isOpen={recurringTaskManagementOpen}
        onClose={() => setRecurringTaskManagementOpen(false)}
      />
    </>
  );
}
